import { Popover } from "antd";
import { BsDatabaseExclamation, BsThreeDots } from "react-icons/bs";
import { cn } from "../utils/functions";
import Menu from "./Menu";

const Table = ({
  columns,
  data,
  loading = false,
  loadingUiSize = 5,
  scroll = {},              // { x?: number | string, y?: number | string }
  headClass,
  nestedRowKey,
  autoFitThreshold = 2024,
}) => {
  let nestedRow = null;

  // Estimate only from provided widths/minWidths (no defaults)
  const estimatedWidth = (columns || []).reduce((sum, col) => {
    const w = toNumber(col?.minWidth) ?? toNumber(col?.width) ?? 0;
    return sum + w;
  }, 0);

  const forceMinWidth = scroll?.x != null ? toNumber(scroll.x) : null;
  const isOverflow = forceMinWidth ? true : estimatedWidth > autoFitThreshold;

  const tableClass = cn(
    "text-slate-700 font-Popins text-xs sm:text-sm md:text-base",
    isOverflow ? "min-w-max" : "w-full table-auto" // show content, no table-fixed
  );

  const tableStyle = {
    minWidth: forceMinWidth ? `${forceMinWidth}px` : undefined,
  };

  const maxHeight = scroll?.y != null ? `${toNumber(scroll.y)}px` : "auto";

  const renderItem = (item, column) => {
    if (column?.key === "action" && column?.render) {
      return (
        <Popover
          content={<Menu items={column.render(item)} />}
          placement="bottomRight"
          arrow={false}
          trigger="click"
        >
          <BsThreeDots className="cursor-pointer text-slate-500 hover:text-slate-800 transition text-base sm:text-lg" />
        </Popover>
      );
    } else if (
      column?.dataIndex &&
      Object.prototype.hasOwnProperty.call(item, column?.dataIndex)
    ) {
      if (column.render)
        return column.render(item[column?.dataIndex], item) || "-";
      else if (typeof item[column?.dataIndex] === "number") {
        return item[column?.dataIndex];
      } else {
        return item[column?.dataIndex] ? item[column?.dataIndex] : "-";
      }
    } else if (column.render && !column?.dataIndex) {
      return column.render(item) || "-";
    }
    return "-";
  };

  const renderNestedRow = (nestedItem, column) => {
    if (column.renderNested) {
      const value = column.nestDataIndex
        ? nestedItem[column.nestDataIndex]
        : nestedItem;
      return column.renderNested(value, nestedItem) ?? "-";
    }
    return "-";
  };

  return (
    <div className="tableContainer w-full overflow-x-auto">
      <div
        style={{ maxHeight }}
        className="shadow-sm bg-white overflow-x-auto overflow-y-auto p-0"
      >
        <table style={tableStyle} className={tableClass}>
          {/* Header */}
          <thead
            className={cn(
              "bg-white rounded-lg text-slate-600 uppercase text-[10px] sm:text-xs tracking-wider border-t border-b border-slate-200 sticky top-0 z-10",
              { [headClass]: headClass }
            )}
          >
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key ?? index}
                  style={{
                    minWidth:
                      toNumber(column?.minWidth) ??
                      toNumber(column?.width) ??
                      undefined, // no default min
                  }}
                  className={cn(
                    "py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold align-middle whitespace-normal"
                  )}
                >
                  {/* show full title */}
                  <span>{column.title || null}</span>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body (data rows) */}
          {!loading && data?.length > 0 && (
            <>
              {data.map((item, index) => {
                const isNest =
                  !!nestedRowKey &&
                  Array.isArray(item?.[nestedRowKey]) &&
                  item[nestedRowKey].length > 1;

                if (isNest) nestedRow = item[nestedRowKey];
                let nestContent = null;

                if (nestedRow) {
                  nestContent = nestedRow.map((nestedItem, nestedIndex) => {
                    if (nestedIndex === 0) return null;
                    return (
                      <tr
                        key={`nested-${index}-${nestedIndex}`}
                        className="bg-slate-50 hover:bg-slate-100 border-b border-slate-100 transition"
                      >
                        {columns.map((column, colIdx) => {
                          if (
                            column?.nestDataIndex &&
                            Object.prototype.hasOwnProperty.call(
                              nestedItem,
                              column?.nestDataIndex
                            )
                          ) {
                            return (
                              <td
                                key={`${column.key ?? colIdx}-nested`}
                                style={{
                                  minWidth:
                                    toNumber(column?.minWidth) ??
                                    toNumber(column?.width) ??
                                    undefined,
                                }}
                                className={cn(
                                  "py-2 px-3 sm:px-4 text-slate-600 whitespace-normal break-words text-[11px] sm:text-sm align-middle",
                                  { [column.tdClass]: column.tdClass }
                                )}
                              >
                                <div>{renderNestedRow(nestedItem, column)}</div>
                              </td>
                            );
                          } else if (column.renderNested && !column?.nestDataIndex) {
                            return (
                              <td
                                key={`${column.key ?? colIdx}-nested-any`}
                                style={{
                                  minWidth:
                                    toNumber(column?.minWidth) ??
                                    toNumber(column?.width) ??
                                    undefined,
                                }}
                                className={cn(
                                  "py-2 px-3 sm:px-4 text-slate-600 whitespace-normal break-words text-[11px] sm:text-sm align-middle",
                                  { [column.tdClass]: column.tdClass }
                                )}
                              >
                                <div>{renderNestedRow(nestedItem, column)}</div>
                              </td>
                            );
                          } else {
                            return null;
                          }
                        })}
                      </tr>
                    );
                  });
                }

                nestedRow = null;

                return (
                  <tbody
                    className="bg-white transition-all duration-200"
                    key={`row-${index}`}
                  >
                    <tr
                      className={cn(
                        "border-slate-200",
                        { "bg-slate-50/50": index % 2 === 1 }
                      )}
                    >
                      {columns.map((column, colIndex) => {
                        const isNestRow =
                          !!nestedRowKey &&
                          Array.isArray(item?.[nestedRowKey]) &&
                          item[nestedRowKey].length > 1;

                        const isSetRowSpan = isNestRow && !column.renderNested;

                        return (
                          <td
                            rowSpan={isSetRowSpan ? item[nestedRowKey].length : 1}
                            key={column.key ?? colIndex}
                            style={{
                              minWidth:
                                toNumber(column?.minWidth) ??
                                toNumber(column?.width) ??
                                undefined,
                            }}
                            className={cn(
                              "py-2 sm:py-3 px-2 sm:px-4 align-middle text-slate-700 text-[11px] sm:text-sm whitespace-normal break-words",
                              {
                                "rounded-bl-lg":
                                  index === data.length - 1 && colIndex === 0,
                              },
                              {
                                "rounded-br-lg":
                                  index === data.length - 1 &&
                                  colIndex === columns.length - 1,
                              },
                              { [column.tdClass]: column.tdClass }
                            )}
                          >
                            <div>{renderItem(item, column)}</div>
                          </td>
                        );
                      })}
                    </tr>
                    {nestContent || null}
                  </tbody>
                );
              })}
            </>
          )}

          {/* Column-wise Skeleton Rows (loading) */}
          {loading && (
            <tbody className="animate-pulse">
              {Array.from({ length: loadingUiSize }).map((_, rIdx) => (
                <tr
                  key={`sk-row-${rIdx}`}
                  className={cn(
                    "border-b border-slate-200",
                    { "bg-slate-50/50": rIdx % 2 === 1 }
                  )}
                >
                  {columns.map((column, cIdx) => (
                    <td
                      key={`sk-cell-${rIdx}-${cIdx}`}
                      style={{
                        minWidth:
                          toNumber(column?.minWidth) ??
                          toNumber(column?.width) ??
                          undefined,
                      }}
                      className="py-2 sm:py-3 px-2 sm:px-4 align-middle"
                    >
                      <div className="h-3 sm:h-4 w-[70%] bg-slate-200 rounded" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {/* Empty State */}
        {!data?.length && !loading && (
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center px-4">
            <BsDatabaseExclamation className="text-slate-300" size={60} />
            <h4 className="mt-2 text-sm sm:text-lg font-medium text-slate-500">
              No Records Found
            </h4>
            <p className="text-slate-400 text-xs sm:text-sm">
              Try adjusting your filters or search query
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;

/** Utils */
function toNumber(v) {
  if (v == null) return undefined;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}
